import { useCallback, useEffect, useState } from 'react'
import { useProductData } from '@/components/ProductData/ProductDataProvider'
import { usePriceIntentQuery } from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { priceIntentServiceInitClientSide } from '@/services/priceIntent/PriceIntentService'
import { useShopSessionId } from '@/services/shopSession/ShopSessionContext'
import { useApolloClient } from '@apollo/client'
import { usePriceTemplate } from './PurchaseForm/priceTemplateAtom'

export const usePriceIntent = (preloadedPriceIntentId?: string | null) => {
  const apolloClient = useApolloClient()
  const priceIntentService = priceIntentServiceInitClientSide(apolloClient, logger)
  const shopSessionId = useShopSessionId()
  const priceTemplate = usePriceTemplate()
  const productData = useProductData()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()

  // Get intended priceIntentId
  const storedPriceIntentId = shopSessionId ? priceIntentService.getStoredId(priceTemplate.name, shopSessionId) : null
  const priceIntentId = preloadedPriceIntentId ?? storedPriceIntentId
  // Save the preloaded priceIntentId to cookies for subsequent usePriceIntent usage
  if (shopSessionId && preloadedPriceIntentId) {
    priceIntentService.save({
      priceIntentId: preloadedPriceIntentId,
      templateName: priceTemplate.name,
      shopSessionId,
    })
  }

  // Fetch it from cache, or server
  const { data: cachedData, loading: queryLoading } = usePriceIntentQuery({
    variables: { priceIntentId: priceIntentId || '' },
    skip: !priceIntentId,
  })

  // Create a priceIntent if it doesn't exist
  useEffect(() => {
    if (priceIntentId || !shopSessionId) {
      return
    }

    const initPriceIntent = async () => {
      try {
        setLoading(true)
        await priceIntentService.create({
          productName: productData.name,
          priceTemplate,
          shopSessionId,
        })
      } catch (e) {
        console.error('Failed to get or create price intent:', e)
        setError(e instanceof Error ? e : new Error('Failed to get or create price intent'))
      } finally {
        setLoading(false)
      }
    }

    initPriceIntent()
  }, [
    priceIntentId,
    apolloClient,
    preloadedPriceIntentId,
    priceTemplate,
    productData.name,
    shopSessionId,
    priceIntentService,
  ])

  const resetPriceIntent = useCallback(() => {
    if (!shopSessionId) {
      throw new Error('Shop session ID is missing')
    }

    priceIntentService.create({
      productName: productData.name,
      priceTemplate,
      shopSessionId,
    })
  }, [priceIntentService, productData.name, priceTemplate, shopSessionId])

  return {
    priceIntentId,
    priceIntent: cachedData?.priceIntent,
    loading: loading || queryLoading,
    error,
    resetPriceIntent,
  }
}

export const usePriceIntentValueOrThrow = (preloadedPriceIntentId?: string) => {
  const { priceIntent } = usePriceIntent(preloadedPriceIntentId)

  if (!priceIntent) {
    throw new Error('Price intent is missing')
  }

  return priceIntent
}
